import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Save, Trash2, RotateCcw, Upload, Loader2 } from 'lucide-react'
import type { Testimonial, TestimonialType } from '../../services/testimonialService'
import type { CreateTestimonialData } from '../../services/testimonialService'
import { uploadImage, uploadMultipleImages, uploadVideo } from '../../lib/storage'

interface TestimonialFormProps {
  testimonial?: Testimonial | null
  isOpen: boolean
  onClose: () => void
  onSave: (testimonialData: CreateTestimonialData) => Promise<void>
  onDelete?: (testimonialId: string) => Promise<void>
  onRestore?: (testimonialId: string) => Promise<void>
  isLoading?: boolean
}

export function TestimonialForm({ 
  testimonial, 
  isOpen, 
  onClose, 
  onSave, 
  onDelete, 
  onRestore, 
  isLoading = false 
}: TestimonialFormProps) {
  const [formData, setFormData] = useState<CreateTestimonialData>({
    type: 'text',
    content: '',
    author_name: '',
    author_location: '',
    order: 0
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (testimonial) {
      setFormData({
        type: testimonial.type,
        content: testimonial.content,
        author_name: testimonial.author_name || '',
        author_location: testimonial.author_location || '',
        order: testimonial.order || 0
      })
    } else {
      setFormData({
        type: 'text',
        content: '',
        author_name: '',
        author_location: '',
        order: 0
      })
    }
    setErrors({})
  }, [testimonial, isOpen])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required'
    }

    if (formData.type === 'youtube' && !formData.content.match(/youtube\.com|youtu\.be/)) {
      newErrors.content = 'Please enter a valid YouTube URL'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    await onSave(formData)
  }

  const handleImageUpload = async (file: File) => {
    setUploading(prev => ({ ...prev, image: true }))
    
    const result = await uploadImage(file, 'testimonials')
    
    setUploading(prev => ({ ...prev, image: false }))
    
    if (result.error) {
      setErrors(prev => ({ ...prev, content: result.error || 'Upload failed' }))
      return
    }
    
    if (result.url) {
      setFormData(prev => ({ ...prev, content: result.url! }))
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.content
        return newErrors
      })
    }
  }

  const handleVideoUpload = async (file: File) => {
    // Validate file size before upload (50MB limit)
    const MAX_VIDEO_SIZE = 50 * 1024 * 1024 // 50MB in bytes
    if (file.size > MAX_VIDEO_SIZE) {
      setErrors(prev => ({ 
        ...prev, 
        content: `File size exceeds 50MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB` 
      }))
      return
    }

    setUploading(prev => ({ ...prev, video: true }))
    
    const result = await uploadVideo(file, 'testimonials')
    
    setUploading(prev => ({ ...prev, video: false }))
    
    if (result.error) {
      setErrors(prev => ({ ...prev, content: result.error || 'Upload failed' }))
      return
    }
    
    if (result.url) {
      setFormData(prev => ({ ...prev, content: result.url! }))
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.content
        return newErrors
      })
    }
  }

  const isUploading = Object.values(uploading).some(value => value === true)

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-2xl font-bold text-foreground">
              {testimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as TestimonialType }))}
                  className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="youtube">YouTube</option>
                </select>
              </div>

              {/* Content based on type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {formData.type === 'youtube' ? 'YouTube URL' :
                   formData.type === 'image' ? 'Image' :
                   formData.type === 'video' ? 'Video' :
                   'Content'} *
                </label>
                
                {formData.type === 'image' ? (
                  <div>
                    {formData.content ? (
                      <div className="mb-2">
                        <img 
                          src={formData.content} 
                          alt="Testimonial" 
                          className="h-32 w-auto rounded-lg border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, content: '' }))}
                          className="mt-2 px-3 py-1 text-sm bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90"
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              handleImageUpload(file)
                            }
                          }}
                          className="hidden"
                          id="testimonial-image"
                        />
                        <label
                          htmlFor="testimonial-image"
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-block"
                        >
                          {uploading.image ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Upload Image
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                ) : formData.type === 'video' ? (
                  <div>
                    {formData.content ? (
                      <div className="mb-2">
                        <video
                          src={formData.content}
                          controls
                          className="h-32 w-auto rounded-lg border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, content: '' }))}
                          className="mt-2 px-3 py-1 text-sm bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90"
                        >
                          Remove Video
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              handleVideoUpload(file)
                            }
                          }}
                          className="hidden"
                          id="testimonial-video"
                        />
                        <label
                          htmlFor="testimonial-video"
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-block"
                        >
                          {uploading.video ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              Upload Video (Max 50MB)
                            </>
                          )}
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">Maximum file size: 50MB</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    rows={formData.type === 'text' ? 6 : 3}
                    className={`w-full px-4 py-2 bg-input border ${
                      errors.content ? 'border-red-500' : 'border-border'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder={
                      formData.type === 'youtube' ? 'https://www.youtube.com/watch?v=...' :
                      'Enter testimonial content...'
                    }
                  />
                )}
                {errors.content && <p className="text-sm text-red-500 mt-1">{errors.content}</p>}
              </div>

              {/* Author Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Author Name (Optional)</label>
                <input
                  type="text"
                  value={formData.author_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                  className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Author Location */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Author Location (Optional)</label>
                <input
                  type="text"
                  value={formData.author_location || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, author_location: e.target.value }))}
                  className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Display Order (Optional)</label>
                <input
                  type="number"
                  value={formData.order || 0}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first</p>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-border">
            {testimonial && (
              <div className="flex items-center gap-2">
                {testimonial.deleted_at ? (
                  <button
                    type="button"
                    onClick={async () => {
                      if (onRestore && testimonial.id) {
                        await onRestore(testimonial.id)
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Restore Testimonial
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      if (onDelete && testimonial.id) {
                        await onDelete(testimonial.id)
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete Testimonial
                  </button>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || isUploading}
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Testimonial
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

